import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CompleteNegotiationDialog = ({ target, onClose, onDone }) => {
  const { getAuthHeader } = useAuth();
  const [finalPrice, setFinalPrice] = useState(target.price.toString());
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !finalPrice) {
      toast.error('Final price and negotiated invoice PDF are required');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('final_price', finalPrice);
      formData.append('file', file);
      await axios.post(`${API}/quotations/${target.id}/complete-negotiation`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Negotiated invoice uploaded — final price recorded');
      onDone();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete negotiation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Negotiated Invoice — {target.vendor_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-text-secondary">
            Original price: <span className="font-semibold text-text-primary">₹{(target.original_price ?? target.price).toFixed(2)}</span>.
            Enter the final agreed price and attach the negotiated invoice PDF.
          </p>
          <div className="space-y-2">
            <Label htmlFor="final-price">Final Negotiated Price (₹)</Label>
            <Input
              id="final-price"
              type="number"
              step="0.01"
              min="0.01"
              value={finalPrice}
              onChange={(e) => setFinalPrice(e.target.value)}
              required
              data-testid="final-price-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="negotiated-pdf">Negotiated Invoice (PDF)</Label>
            <Input
              id="negotiated-pdf"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
              data-testid="negotiated-pdf-input"
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white"
            disabled={submitting}
            data-testid="confirm-complete-negotiation-button"
          >
            {submitting ? 'Uploading...' : 'Finalize Negotiation'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteNegotiationDialog;
